import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Filter, Search, SlidersHorizontal } from 'lucide-react';
import LaborOperationsLayout from '../../../components/labor/LaborOperationsLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import {
  getAdminLaborCases,
  getLaborAdminAccess,
  laborAdminStatuses,
  laborAssignees,
} from '../../../features/labor-intake/laborIntakeAdminService';

const toDate = (value) =>
  value ? new Intl.DateTimeFormat('ar-SA', { dateStyle: 'medium' }).format(new Date(value)) : '-';

const clientName = (item) =>
  item.client?.fullName ||
  [item.client?.firstName, item.client?.fatherName, item.client?.familyName].filter(Boolean).join(' ') ||
  '-';

const companyName = (item) => item.company?.tradeName || item.company?.legalName || '-';

const summaryItems = [
  { key: 'new', title: 'طلبات جديدة' },
  { key: 'under_review', title: 'تحت المراجعة' },
  { key: 'missing_required', title: 'نواقص مطلوبة' },
  { key: 'assigned_lawyer', title: 'محالة للمحامين' },
];

const roleLabels = {
  manager: 'إدارة القضايا',
  lawyer: 'مراجعة قانونية',
  intake: 'استقبال الطلبات',
  intern: 'مهام مسندة',
};

const LaborIntakeAdminPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAdminAuth();
  const access = getLaborAdminAccess(user);
  const [cases, setCases] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    assignee: 'all',
    missingOnly: false,
    submittedFrom: '',
  });
  const query = searchParams.get('q') || '';
  const status = searchParams.get('status') || 'all';

  useEffect(() => {
    getAdminLaborCases(user, token)
      .then(setCases)
      .catch((error) => setLoadError(error.message));
  }, [token, user]);

  const stats = useMemo(() => {
    const totals = { total: cases.length };
    laborAdminStatuses.forEach((entry) => {
      totals[entry.value] = cases.filter((item) => item.adminData.status === entry.value).length;
    });
    return totals;
  }, [cases]);

  const filteredCases = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return cases.filter((item) => {
      const haystack = [item.referenceNumber, clientName(item), item.client?.mobile, companyName(item)].join(' ').toLowerCase();
      return (
        (!normalizedQuery || haystack.includes(normalizedQuery)) &&
        (status === 'all' || item.adminData.status === status) &&
        (filters.assignee === 'all' || item.adminData.assigneeId === filters.assignee) &&
        (!filters.missingOnly || item.adminData.missingItems.some((missing) => missing.status === 'required')) &&
        (!filters.submittedFrom || new Date(item.submittedAt) >= new Date(filters.submittedFrom))
      );
    });
  }, [cases, filters, query, status]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  return (
    <LaborOperationsLayout searchValue={query} onSearchChange={(value) => updateParam('q', value)}>
      <main className="mx-auto max-w-[1500px] px-6 pb-14 pt-10 sm:px-12" dir="rtl">
        <section className="lo-hero relative overflow-hidden px-1 pb-10 pt-2">
          <img src="/images/wazin-mark.png" alt="" aria-hidden="true" className="pointer-events-none absolute -left-7 -top-14 h-56 w-56 object-contain opacity-[0.045] mix-blend-multiply" />
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div>
              <p className="lo-kicker font-bold">LABOR CASE WORKSPACE</p>
              <h1 className="lo-heading legal-title mt-4 font-bold">إدارة طلبات القضايا العمالية</h1>
              <p className="lo-lead mt-3">مساحة تشغيلية لمراجعة الملفات وتجهيزها للعمل القانوني.</p>
            </div>
            <div className="rounded-xl bg-[#ece9e2] px-6 py-4">
              <p className="text-[13px] text-[#786f63]">مساحة العمل الحالية</p>
              <p className="mt-1 text-[15px] font-bold text-[#293a50]">{roleLabels[access.role]}</p>
            </div>
          </div>
          <div className="mt-10 grid gap-px overflow-hidden rounded-2xl bg-[#e4dfd6] p-px sm:grid-cols-2 xl:grid-cols-4">
            {summaryItems.map((entry) => (
              <button
                key={entry.key}
                type="button"
                onClick={() => updateParam('status', status === entry.key ? 'all' : entry.key)}
                className={`group px-6 py-5 text-right transition duration-200 ${status === entry.key ? 'bg-[#fffdfa]' : 'bg-[#f3f1eb] hover:bg-[#faf8f3]'}`}
              >
                <p className="text-[14px] font-medium text-[#697483]">{entry.title}</p>
                <p className={`mt-3 text-[36px] font-bold ${status === entry.key ? 'text-[#948066]' : 'text-[#293a50]'}`}>{stats[entry.key] || 0}</p>
              </button>
            ))}
          </div>
        </section>

        <section className="lo-surface mt-10 overflow-hidden rounded-[18px]">
          {loadError && <p className="mx-7 mt-6 rounded-xl bg-[#f8eeee] px-5 py-4 text-sm font-bold text-[#896264]">{loadError}</p>}
          <div className="flex flex-wrap items-center justify-between gap-5 border-b border-[#e7e2d9] px-7 py-6">
            <div className="flex items-center gap-3">
              <h2 className="legal-title text-[21px] font-bold text-[#293a50]">ملفات الطلبات</h2>
              <span className="rounded-lg bg-[#eee8dd] px-3 py-1 text-[13px] font-bold text-[#806f58]">{filteredCases.length}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="lo-control flex h-12 items-center gap-2 rounded-lg px-4 lg:hidden">
                <Search className="h-4 w-4 text-slate-400" />
                <input value={query} onChange={(event) => updateParam('q', event.target.value)} placeholder="بحث..." className="w-44 bg-transparent text-sm outline-none" />
              </label>
              <select value={status} onChange={(event) => updateParam('status', event.target.value)} className="lo-control h-12 rounded-xl px-4 font-semibold">
                <option value="all">كل الحالات</option>
                {laborAdminStatuses.map((entry) => <option key={entry.value} value={entry.value}>{entry.label}</option>)}
              </select>
              <select value={filters.assignee} onChange={(event) => setFilters((current) => ({ ...current, assignee: event.target.value }))} className="lo-control h-12 rounded-xl px-4 font-semibold">
                <option value="all">كل المسؤولين</option>
                <option value="">غير مسند</option>
                {laborAssignees.map((assignee) => <option key={assignee.id} value={assignee.id}>{assignee.name}</option>)}
              </select>
              <label className="lo-control flex h-12 items-center gap-2 rounded-xl px-4 font-semibold">
                <Filter className="h-3.5 w-3.5" />
                <input type="checkbox" checked={filters.missingOnly} onChange={(event) => setFilters((current) => ({ ...current, missingOnly: event.target.checked }))} className="accent-[#948066]" />
                النواقص فقط
              </label>
              <input type="date" value={filters.submittedFrom} onChange={(event) => setFilters((current) => ({ ...current, submittedFrom: event.target.value }))} className="lo-control h-12 rounded-xl px-4" />
              <SlidersHorizontal className="h-4 w-4 text-slate-400" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="lo-table min-w-[1160px] w-full">
              <thead className="text-right">
                <tr>
                  {['الملف', 'العميل والمنشأة', 'التواصل', 'الحالة', 'اكتمال الملف', 'تاريخ التقديم', 'المسؤول', ''].map((header) => (
                    <th key={header} className="border-b px-7 py-5">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCases.map((item) => (
                  <tr key={item.id} onClick={() => navigate(`/admin/labor-intake/${item.id}`)} className="group cursor-pointer border-b transition">
                    <td className="whitespace-nowrap px-7 py-5">
                      <p className="font-bold text-[#273950]">{item.referenceNumber}</p>
                      <p className="mt-1 text-[13px] text-[#697483]">{toDate(item.lastUpdatedAt)}</p>
                    </td>
                    <td className="px-7 py-5">
                      <p className="font-bold text-[#273950]">{clientName(item)}</p>
                      <p className="mt-1 text-[14px] text-[#697483]">{companyName(item)}</p>
                    </td>
                    <td className="whitespace-nowrap px-7 py-5 text-[14px] text-[#697483]" dir="ltr">{item.client?.mobile || '-'}</td>
                    <td className="px-7 py-5">
                      <span className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-bold ${item.statusInfo.tone}`}>{item.statusInfo.label}</span>
                    </td>
                    <td className="px-7 py-5">
                      {access.role === 'intake' ? '-' : (
                        <div className="flex items-center gap-3">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-[#ede6db]">
                            <div className="h-full rounded-full bg-[#948066]" style={{ width: `${item.completion}%` }} />
                          </div>
                          <span className="text-[14px] font-bold text-[#697483]">{item.completion}%</span>
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-7 py-5 text-[14px] text-[#697483]">{toDate(item.submittedAt)}</td>
                    <td className="whitespace-nowrap px-7 py-5 text-[14px] font-bold text-[#293a50]">{access.role === 'intake' ? '-' : item.assigneeName}</td>
                    <td className="px-7 py-5"><ArrowLeft className="h-4 w-4 text-[#a39a8e]" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!filteredCases.length && <div className="px-8 py-20 text-center text-sm text-slate-400">لا توجد ملفات مطابقة لمعايير العرض الحالية.</div>}
        </section>
      </main>
    </LaborOperationsLayout>
  );
};

export default LaborIntakeAdminPage;
